import { createContext, useContext } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DragOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import styles from "./SortableDragRow.module.css";

type UseSortableReturn = ReturnType<typeof useSortable>;

type RowContextProps = {
  setActivatorNodeRef?: UseSortableReturn["setActivatorNodeRef"];
  listeners?: UseSortableReturn["listeners"];
};

export const RowContext = createContext<RowContextProps>({});

type DragHandleProps = {
  saving?: boolean;
};

export const DragHandle = ({ saving }: DragHandleProps) => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  if (saving) return <Spin size="small" />;
  return (
    <span ref={setActivatorNodeRef} {...listeners} className={styles.handle}>
      <DragOutlined />
    </span>
  );
};

type SortableDragRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
  "data-row-key": string;
};

export const SortableDragRow = ({ children, ...props }: SortableDragRowProps) => {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id: props["data-row-key"] ?? "" });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { opacity: 0.5, position: "relative", zIndex: 999 } : {}),
  };

  return (
    <RowContext.Provider value={{ setActivatorNodeRef, listeners }}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes}>
        {children}
      </tr>
    </RowContext.Provider>
  );
};
